import {
  createContext,
  Dispatch,
  SetStateAction,
  useMemo,
  useState,
} from "react";
import { aspectRatioEnumToNumber } from "./helpers";
import { ThemeEditor } from "./components/ThemeEditor";
import { createTemplateEditorStore } from "../storyboardEditor/store/useTemplateEditorStore";
import { useList } from "@refinedev/core";
import {
  AssetLibrary,
  BrandKit,
  BrandKitAsset,
  PresenterProfileResponse,
  Slide,
} from "../../types";
import { useSession } from "hooks/useSession/";
import {
  PreviewSizes,
  usePreviewResize,
} from "../storyboardEditor/hooks/usePreviewResize";
import { DEFAULT_MEDIA_VIDEO_ASPECT_RATIO } from "../../constants";
import { useQuery } from "@tanstack/react-query";
import { loadImageRatio } from "../storyboardEditor/helpers";
import {
  TemplateDataContext,
  TemplateDataContextType,
  TemplateEditorStoreContext,
} from "providers/TemplateDataProvider/";
import { useMediaAssets } from "pages/asset-libraries/useMediaAssets/";
import {
  usePublicThemes,
  useOrgThemes,
  type ThemeResponse,
} from "./hooks/useThemeApi";
import placeholders from "./placeholders";

type ThemeDataContextType = {
  organizationId: string | undefined;
  brandKits: BrandKit[];
  activeBrandKit: BrandKit | undefined;
  setBrandKit: Dispatch<SetStateAction<BrandKit>>;
  logo: BrandKitAsset | undefined;
  logoRatio: number | undefined;
  presenters: PresenterProfileResponse[];
  activePresenter: PresenterProfileResponse | undefined;
  presenterRatio: number | undefined;
  setPresenter: Dispatch<SetStateAction<PresenterProfileResponse | undefined>>;
  previewData: {
    previewSizes: PreviewSizes | undefined;
  };
  mediaAspectRatio: number;
  publicThemes: ThemeResponse[];
  orgThemes: ThemeResponse[];
  selectedTheme: ThemeResponse | null;
  setSelectedTheme: Dispatch<SetStateAction<ThemeResponse | null>>;
  isPublicTheme: boolean;
  slidesPlaceholders: Slide[];
  updateSlidePlaceholders: Dispatch<SetStateAction<Slide[]>>;
  isThemesLoading: boolean;
};

export const ThemeDataContext = createContext<ThemeDataContextType | null>(
  null
);

const store = createTemplateEditorStore();

export const ThemeEditorPage = () => {
  const { currentOrganization } = useSession();

  const { data: publicThemesData, isLoading: isPublicThemesLoading } =
    usePublicThemes();
  const { data: orgThemesData, isLoading: isOrgThemesLoading } = useOrgThemes(
    currentOrganization?.id
  );

  const publicThemes = useMemo(
    () => publicThemesData?.items ?? [],
    [publicThemesData?.items]
  );
  const orgThemes = useMemo(
    () => orgThemesData?.items ?? [],
    [orgThemesData?.items]
  );

  const [selectedTheme, setSelectedTheme] = useState<ThemeResponse | null>(
    null
  );
  const isPublicTheme = selectedTheme?.public ?? false;

  const { data: brandKits } = useList<BrandKit>({
    resource: `media/${currentOrganization?.id}/brand_kits?include_generated=false`,
    queryOptions: {
      enabled: !!currentOrganization?.id,
    },
  });

  const { data: presenters } = useList<PresenterProfileResponse>({
    resource: `media/${currentOrganization?.id}/presenter_profiles`,
    queryOptions: {
      enabled: !!currentOrganization?.id,
    },
  });

  const { data: assetLibraries } = useList<AssetLibrary>({
    resource: `media/${currentOrganization?.id}/asset_libraries`,
    queryOptions: {
      enabled: !!currentOrganization?.id,
    },
    errorNotification: false,
  });

  const mediaAssetsData = useMediaAssets(
    assetLibraries?.data[0]?.id,
    currentOrganization?.id
  );

  const allBrandKits = useMemo(
    () => [placeholders.DEFAULT_BRAND_KIT, ...(brandKits?.data ?? [])],
    [brandKits?.data]
  );

  const [activeBrandKit, setBrandKit] = useState<BrandKit>(
    placeholders.DEFAULT_BRAND_KIT
  );

  const [presenter, setPresenter] = useState<
    PresenterProfileResponse | undefined
  >(undefined);

  const activePresenter =
    presenter ?? presenters?.data.find((presenter) => presenter.face_profile);

  const presenterPath = useMemo(
    () =>
      activePresenter?.face_profile?.extracted_asset_path ??
      activePresenter?.face_profile?.custom_asset_path ??
      undefined,
    [
      activePresenter?.face_profile?.extracted_asset_path,
      activePresenter?.face_profile?.custom_asset_path,
    ]
  );

  const { data: presenterRatio } = useQuery<number>({
    queryKey: ["imageRatio", "presenter", presenterPath],
    queryFn: () => loadImageRatio(presenterPath!),
    enabled: !!presenterPath,
  });

  const logo = useMemo(
    () =>
      activeBrandKit.assets.find(
        (asset) => asset.type === "Picture" && asset.purpose === "Logo"
      ),
    [activeBrandKit.assets]
  );

  const { data: logoRatio } = useQuery<number>({
    queryKey: ["imageRatio", "logo", logo?.path],
    queryFn: () => loadImageRatio(logo!.path),
    enabled: !!logo?.path,
  });

  const mediaAspectRatio = useMemo(
    () =>
      selectedTheme
        ? aspectRatioEnumToNumber(selectedTheme.aspect_ratio)
        : DEFAULT_MEDIA_VIDEO_ASPECT_RATIO,
    [selectedTheme]
  );

  const [slidesPlaceholders, updateSlidePlaceholders] = useState<Slide[]>(
    placeholders.DEFAULT_SLIDES_PLACEHOLDERS
  );

  const { previewSizes } = usePreviewResize(mediaAspectRatio);

  const contextValue = useMemo(
    () => ({
      organizationId: currentOrganization?.id,
      brandKits: allBrandKits,
      activeBrandKit,
      setBrandKit,
      logo,
      logoRatio,
      presenters: presenters?.data ?? [],
      activePresenter,
      setPresenter,
      presenterRatio,
      previewData: {
        previewSizes,
      },
      mediaAspectRatio,
      publicThemes,
      orgThemes,
      selectedTheme,
      setSelectedTheme,
      isPublicTheme,
      slidesPlaceholders,
      updateSlidePlaceholders,
      isThemesLoading: isPublicThemesLoading || isOrgThemesLoading,
    }),
    [
      currentOrganization?.id,
      activeBrandKit,
      allBrandKits,
      setBrandKit,
      logo,
      logoRatio,
      activePresenter,
      presenterRatio,
      presenters?.data,
      setPresenter,
      previewSizes,
      mediaAspectRatio,
      publicThemes,
      orgThemes,
      selectedTheme,
      isPublicTheme,
      slidesPlaceholders,
      updateSlidePlaceholders,
      isPublicThemesLoading,
      isOrgThemesLoading,
    ]
  );

  const brandKitAssets = activeBrandKit.assets;

  // Used for reused components from the storyboard editor that call the useTemplateData hook. Since we don't want to pass all the data, we are only adding the necessary data and therefore we need to cast the type to the TemplateDataContextType.
  const templateDataBridgeValue = useMemo(
    () =>
      ({
        organizationId: currentOrganization?.id,
        brandKitData: {
          brandKit: { data: activeBrandKit },
          brandKitPalette: activeBrandKit.palette,
          logo,
          titleFont: brandKitAssets.find((asset) => asset.purpose === "Title"),
          subtitlesFont: brandKitAssets.find(
            (asset) => asset.purpose === "Subtitles"
          ),
          textFont: brandKitAssets.find((asset) => asset.purpose === "Text"),
        },
        assetLibrariesData: {
          organizationAssetLibraries: assetLibraries?.data ?? [],
        },
        mediaAssetsData,
        mediaGenerationSettingsData: {
          mediaAspectRatio,
        },
        storyboardWithSlidesData: {
          slides: slidesPlaceholders,
          setSlide: (uuid: string, slide: Slide) => {
            updateSlidePlaceholders((prev) =>
              prev.map((s) => (s.uuid === uuid ? slide : s))
            );
          },
        },
        templatesData: {
          presenter: activePresenter,
        },
        previewData: {
          previewSizes,
        },
        placeholderAssets: placeholders.PLACEHOLDER_ASSET_PATHS,
      }) as unknown as TemplateDataContextType,
    [
      currentOrganization?.id,
      activeBrandKit,
      brandKitAssets,
      logo,
      mediaAspectRatio,
      activePresenter,
      previewSizes,
      assetLibraries?.data,
      mediaAssetsData,
      slidesPlaceholders,
    ]
  );

  return (
    <TemplateEditorStoreContext.Provider value={store}>
      <ThemeDataContext.Provider value={contextValue}>
        <TemplateDataContext.Provider value={templateDataBridgeValue}>
          <ThemeEditor />
        </TemplateDataContext.Provider>
      </ThemeDataContext.Provider>
    </TemplateEditorStoreContext.Provider>
  );
};
